import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useCanEditCompany } from '@/hooks/use-permissions'
import {
  AddBoardMemberModal,
  type BoardMemberData,
} from './add-board-member-modal'
import { useUpdateCompany } from '@/hooks/use-companies'
import { showSuccess, showError } from '@/lib/toast'
import type { Company } from '@/types/api'

interface BoardMemberSectionProps {
  company: Company
  onUpdate?: () => void
}

export function BoardMemberSection({
  company,
  onUpdate,
}: BoardMemberSectionProps) {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [optimisticMembers, setOptimisticMembers] = React.useState<
    BoardMemberData[] | null
  >(null)
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(
    company.primaryOwner?.id,
    company.secondaryOwner?.id
  )
  const updateCompany = useUpdateCompany()

  const serverBoardMembers = company.boardMembers
  // Use optimistic members if set, otherwise use server data
  const boardMembers = optimisticMembers ?? serverBoardMembers
  const hasBoardMembers = boardMembers && boardMembers.length > 0
  const currentBoardMemberIds = serverBoardMembers?.map((m) => m.id) ?? []

  // Clear optimistic state when server data changes
  React.useEffect(() => {
    setOptimisticMembers(null)
  }, [serverBoardMembers])

  const handleSave = async (userIds: string[], members: BoardMemberData[]) => {
    if (!canEdit) {
      showError('You do not have permission to update board members')
      return
    }

    // Optimistically update the UI immediately
    setOptimisticMembers(members)

    try {
      await updateCompany.mutateAsync({
        id: company.id,
        data: {
          // Note: This requires backend support for boardMemberIds field
          // in UpdateCompanyDto. If not available, this will be a no-op.
          boardMemberIds: userIds,
        } as Parameters<typeof updateCompany.mutateAsync>[0]['data'],
      })
      showSuccess('Board members updated successfully')
      onUpdate?.()
    } catch {
      // Revert optimistic update on error
      setOptimisticMembers(null)
      showError('Failed to update board members')
      throw new Error('Failed to update board members')
    }
  }

  const handleOpenModal = () => {
    if (!canEdit) {
      showError('You do not have permission to update board members')
      return
    }

    setIsModalOpen(true)
  }

  return (
    <div className="space-y-3">
      <Typography variant="h6">Greybull Board Members:</Typography>

      {hasBoardMembers ? (
        <div>
          <div className="mb-3 flex flex-wrap gap-2">
            {boardMembers.map((member) => (
              <Badge key={member.id}>{member.name}</Badge>
            ))}
            {canEdit ? (
              <Button
                variant="link"
                className="h-auto p-0 text-xs text-primary/70 underline hover:text-primary"
                onClick={handleOpenModal}
              >
                Edit
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-primary/20 bg-white/50 p-3 dark:bg-gray-900">
          <div>
            <Typography className="text-sm text-primary">
              No board members have been added yet
            </Typography>
            <Typography className="text-xs text-primary/70">
              Add Greybull representatives to track board participation for this
              company.
            </Typography>
            {canEdit ? (
              <Button
                type="button"
                variant="badge"
                size="badge"
                className="mt-2"
                onClick={handleOpenModal}
              >
                Add board member +
              </Button>
            ) : null}
          </div>
        </div>
      )}

      <AddBoardMemberModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        currentBoardMemberIds={currentBoardMemberIds}
        onSave={handleSave}
        canEdit={canEdit}
        isEditing={hasBoardMembers}
      />
    </div>
  )
}
